import React from 'react';

import { PageNotFoundWrapper } from '@shared/components/PageNotFound/PageNotFoundWrapper';
import { useSetEventHandle } from '@shared/utils/eventInfo';
import { EXCLUDED_EVENT_HANDLES } from '@shared/constants/excludedEventHandles';

export interface EventRouteProps {
  /**
   * The event handle for the route.
   */
  eventHandle: string;

  /**
   * Optional component to render which will automatically be passed the eventHandle prop.
   *
   * Defaults to rendering `children` if not provided.
   */
  ComponentToRender?: React.ComponentType<{ eventHandle: string }>;

  /**
   * Rendered when no `ComponentToRender` is given. Declared explicitly rather than inherited from
   * `React.FC`, because `React.memo` infers its props from the component's own type and would
   * otherwise reject a `children` prop at every call site.
   *
   * Pass a stable element (hoisted to a module constant) — a fresh one each render defeats the memo.
   */
  children?: React.ReactNode;
}

/**
 * Memoised because it sits directly under the root `<Switch>`, which re-renders on every URL change
 * — including the ones Aloha's scroll spy writes while the guest scrolls. Without the bail-out, each
 * of those re-rendered the entire app below this point. Callers must pass a stable `children`
 * element (hoist it to a module constant) for the memo to fire.
 */
const EventRouteComponent = ({ eventHandle, ComponentToRender, children }: EventRouteProps) => {
  const isExcludedEventHandle = EXCLUDED_EVENT_HANDLES.has(eventHandle);
  useSetEventHandle(isExcludedEventHandle ? undefined : eventHandle);

  if (isExcludedEventHandle) {
    return <>{ComponentToRender ? <ComponentToRender eventHandle={eventHandle} /> : children}</>;
  }

  return <PageNotFoundWrapper eventHandle={eventHandle}>{ComponentToRender ? <ComponentToRender eventHandle={eventHandle} /> : children}</PageNotFoundWrapper>;
};

export const EventRoute = React.memo(EventRouteComponent);
